import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DropdownBody, DropdownItem, Icons, useToast } from "fds";
import { DocInfo, FileShareLinkPolicy, LogItem } from "../../../types/files";
import { t } from "i18next";
import { handleDocDownload, handleDocPreview } from "../../../shared/utils/api";
import useDocumentStore from "../../../redux/dispatcher/useDocumentStore";
import ShareLinkModal from "../../../components/More/ShareLinkModal";
import ReadRequestModal from "../../../components/More/ReadRequestModal";
import { nanoid } from "@reduxjs/toolkit";
import useLogStore from "../../../redux/dispatcher/useLogStore";
import ChangeWorkgroupFileAuthModal from "../../../components/More/ChangeWorkgroupFileAuthModal";
import utilsCommon from "../../../shared/utils/common";
interface LogSettingButtonProps {
  settingButtonInfo: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
    docInfo?: DocInfo;
  };
  setSettingInfo: (req: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
    docInfo?: DocInfo;
  }) => void;
}

const LogSettingButton: React.FC<LogSettingButtonProps> = ({
  settingButtonInfo,
  setSettingInfo,
}) => {
  const toast = useToast();
  const [modalType, changeModalType] = useState("CLOSE");

  const { getContentDownload, getContentDetail } = useDocumentStore();
  const { getLogDetail } = useLogStore();
  let menus = [
    {
      key: "preview",
      resource: t("file.action.open"),
      icon: <Icons variant="line" label="preview" isOn />,
      historyType: 1,
      isHidden: !(
        utilsCommon.checkLicenseAvailable("allowWebView") &&
        utilsCommon.checkExtensionAvailable(
          settingButtonInfo?.docInfo?.contentName ??
            settingButtonInfo?.log?.documnetName ??
            "",
          "fwocExtensions"
        )
      ),
      onClick: () => checkDocValid(1, "preview"),
    },
    {
      key: "download",
      resource: t("file.action.download"),
      historyType: 1,
      icon: <Icons variant="line" label="download" />,
      onClick: () => checkDocValid(1, "download"),
    },
    {
      key: "shareLink",
      resource: t("file.action.shareLink"),
      icon: <Icons variant="line" label="link" />,
      historyType: 1,
      onClick: () => checkDocValid(1, "SHARE_LINK"),
    },
    {
      key: "auth",
      resource: t("file.action.auth"),
      icon: <Icons variant="line" label="access" />,
      historyType: 1,
      onClick: () => checkDocValid(1, "CHANGE_FILE_AUTH"),
    },
    {
      key: "requestOpen",
      resource: t("file.action.sendReadRequest"),
      icon: <Icons variant="line" label="alertOctagon" />,
      historyType: 1,
      onClick: () => checkDocValid(1, "READ_REQUEST"),
    },
  ];
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'
  const inputRef = useRef<HTMLTableRowElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!inputRef.current) return;
    const observer = new ResizeObserver(() => {
      if (inputRef.current) {
        setHeight(inputRef.current.offsetHeight);
      }
    });
    observer.observe(inputRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (settingButtonInfo.log) return;
    getLogDetail({ logIdList: [settingButtonInfo.logId] }).then((resp: any) =>
      setSettingInfo({ ...settingButtonInfo, log: resp.payload.logResults[0] })
    );
  }, [settingButtonInfo.logId]);
  const checkDocValid = async (historyType: number, action: string) => {
    if (!settingButtonInfo?.log) return;

    if (historyType === 1) {
      let resp: any = await getContentDetail({
        workgroupId: settingButtonInfo?.log?.workgroupId,
        syncId: settingButtonInfo.log?.syncId,
      });
      if (resp.payload) {
        if (
          !resp.payload.documentInfo ||
          resp.payload.documentInfo.status !== "N"
        ) {
          toast.toastMsg(nanoid(), t("file.alert.contentDelete"), "error");
          return;
        }
        setSettingInfo({
          ...settingButtonInfo,
          docInfo: resp.payload.documentInfo,
        });
      }
      switch (action) {
        case "preview":
          return handleDocPreview({
            permission: "view",
            workgroupId: settingButtonInfo.log?.workgroupId,
            syncId: settingButtonInfo?.log.syncId,
            toast,
            t,
            revisionNo: settingButtonInfo.log?.revisionNo,
          });
        case "download":
          return handleDocDownload(
            settingButtonInfo.log?.syncId,
            getContentDownload,
            toast,
            t
          );
        case "SHARE_LINK":
          return changeModalType("SHARE_LINK");
        case "CHANGE_FILE_AUTH":
          return changeModalType("CHANGE_FILE_AUTH");
        case "READ_REQUEST":
          return changeModalType("READ_REQUEST");
      }
    }
  };
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });
  const handleTurnOffSettng = (): void => {
    setSettingInfo({
      x: 0,
      y: 0,
      logId: "",
    });
  };
  return modalType === "CLOSE" ? (
    <DropdownBody
      maxWidth={300}
      dropdownRef={inputRef}
      minWidth={190}
      width={"fit-content"}
      location={{
        x:
          settingButtonInfo.x -
          (direction === "ltr" && settingButtonInfo.x > window.innerWidth / 2
            ? 190
            : 0) -
          (direction === "rtl" && window.eco.companyName === "linkApp"
            ? 38
            : 0),
        y:
          settingButtonInfo.y -
          (settingButtonInfo.y > window.innerHeight / 2 ? height : 0),
      }}
    >
      {menus
        .filter(
          (item) =>
            settingButtonInfo.log?.historyType === item.historyType &&
            !item.isHidden
        )
        .map((item) => (
          <DropdownItem
            height={item.key === "divider" ? undefined : 20}
            type={item.key.includes("delete") ? "warning" : "default"}
            state={item.key === "divider" ? "divider" : "default"}
            label={item.resource}
            frontIcon={item.icon}
            onClick={item.onClick}
          />
        ))}
    </DropdownBody>
  ) : (
    <>
      {modalType === "SHARE_LINK" && (
        <ShareLinkModal
          open={modalType === "SHARE_LINK"}
          onClose={handleTurnOffSettng}
          docInfo={settingButtonInfo.docInfo}
        />
      )}
      {modalType === "READ_REQUEST" && (
        <ReadRequestModal
          open={modalType === "READ_REQUEST"}
          onClose={handleTurnOffSettng}
          docInfo={settingButtonInfo.docInfo}
        />
      )}
      {modalType === "CHANGE_FILE_AUTH" && (
        <ChangeWorkgroupFileAuthModal
          open={modalType === "CHANGE_FILE_AUTH"}
          onClose={handleTurnOffSettng}
          docInfos={
            settingButtonInfo.docInfo ? [settingButtonInfo.docInfo] : undefined
          }
        />
      )}
    </>
  );
};
export default LogSettingButton;
